import {
  isAlreadyNumbered,
  numFmtToListStyleType,
  type NumberedParagraphMeta,
} from "../ooxml/numberingCore";
import { markImportedOrderedListElement } from "../../editor/listNumbering";
import { collectImportBlockElements, escapeHtml } from "./collectBlocks";
import type { NumberingFidelityStats } from "../types";

const EMPTY_STATS: NumberingFidelityStats = {
  paragraphsInDocx: 0,
  paragraphsInHtml: 0,
  numberedInDocx: 0,
  numbersApplied: 0,
  listsStructured: 0,
  multilevelApplied: 0,
};

function normalizedLeadingText(el: HTMLElement): string {
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
}

function textStartsWithNumber(text: string, computed: string): boolean {
  const c = computed.trim();
  if (!c) return false;
  return text.startsWith(c) || text.startsWith(c.replace(/\.$/, ""));
}

function prependComputedNumber(el: HTMLElement, computed: string): void {
  const label = computed.trim();
  if (!label) return;

  const existing = normalizedLeadingText(el);
  if (textStartsWithNumber(existing, label)) return;

  const prefix = `<span data-lexgat-num="true"><strong>${escapeHtml(label)}</strong></span> `;

  if (el.tagName === "LI") {
    const firstP = el.querySelector(":scope > p");
    if (firstP instanceof HTMLElement) {
      firstP.innerHTML = prefix + firstP.innerHTML;
    } else {
      el.innerHTML = prefix + el.innerHTML;
    }
    return;
  }

  el.innerHTML = prefix + el.innerHTML;
}

function applyIlvlIndent(el: HTMLElement, ilvl: number): void {
  if (ilvl <= 0) return;
  const extraPx = ilvl * 24;
  const current = parseInt(el.style.marginLeft || "0", 10) || 0;
  el.style.marginLeft = `${current + extraPx}px`;
}

interface ListRun {
  numId: number;
  ilvl: number;
  numFmt: string | null;
  elements: HTMLElement[];
  startValue: number | null;
  isNewInstance: boolean;
  lastCounter: number | null;
}

function parseLeadingCounter(text: string): number | null {
  const m = text.match(/^(\d+)[.)]/);
  return m ? parseInt(m[1], 10) : null;
}

function shouldStartNewDocxInstance(
  currentRun: ListRun | null,
  meta: NumberedParagraphMeta,
  visibleCounter: number | null,
): boolean {
  if (!currentRun) return (meta.ilvl ?? 0) === 0;
  if ((meta.ilvl ?? 0) > 0) return false;
  if (
    visibleCounter != null &&
    currentRun.lastCounter != null &&
    visibleCounter < currentRun.lastCounter
  ) {
    return true;
  }
  if (
    visibleCounter === 1 &&
    currentRun.lastCounter != null &&
    currentRun.lastCounter > 1
  ) {
    return true;
  }
  return false;
}

/**
 * Wrap consecutive same-level decimal paragraphs in TipTap-compatible <ol> lists.
 * Skipped when Word uses custom lvlText (e.g. "(a)") that CSS list-style cannot reproduce.
 */
function structureDecimalListRuns(
  doc: Document,
  runs: ListRun[],
): number {
  let structured = 0;

  for (const run of runs) {
    if (run.elements.length < 2) continue;
    if (run.ilvl !== 0) continue;
    if (run.elements.some((el) => el.closest("ol, ul"))) continue;

    const listStyle = numFmtToListStyleType(run.numFmt);
    if (listStyle !== "decimal") continue;

    const firstText = normalizedLeadingText(run.elements[0]);
    if (/\(\s*[a-z]\s*\)/i.test(firstText)) continue;

    const ol = doc.createElement("ol");
    ol.style.listStyleType = listStyle;
    if (run.startValue != null && run.startValue > 1) {
      ol.setAttribute("start", String(run.startValue));
    }
    if (run.isNewInstance) {
      markImportedOrderedListElement(ol, { isNewInstance: true });
    }

    const parent = run.elements[0].parentElement;
    if (!parent) continue;

    parent.insertBefore(ol, run.elements[0]);

    for (const el of run.elements) {
      const li = doc.createElement("li");
      if (el.tagName === "LI") {
        li.innerHTML = el.innerHTML;
      } else {
        li.innerHTML = el.innerHTML;
      }
      const numSpan = li.querySelector('[data-lexgat-num="true"]');
      numSpan?.remove();
      ol.appendChild(li);
      el.remove();
    }

    structured++;
  }

  return structured;
}

/**
 * Apply OOXML numbering to mammoth HTML (Phase 2).
 * Prepends Word-computed labels; optionally groups decimal sequences into <ol>.
 */
export function applyNumberingToHtml(
  html: string,
  metaList: NumberedParagraphMeta[],
): { html: string; stats: NumberingFidelityStats } {
  const stats: NumberingFidelityStats = { ...EMPTY_STATS };
  stats.paragraphsInDocx = metaList.length;
  stats.numberedInDocx = metaList.filter((m) => m.computedNumber).length;

  if (!html.trim() || stats.numberedInDocx === 0) {
    return { html, stats };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="num-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("num-root");
  if (!root) return { html, stats };

  const blocks = collectImportBlockElements(root);
  stats.paragraphsInHtml = blocks.length;

  const listRuns: ListRun[] = [];
  let currentRun: ListRun | null = null;

  const limit = Math.min(blocks.length, metaList.length);
  for (let i = 0; i < limit; i++) {
    const el = blocks[i];
    const meta = metaList[i];
    if (!meta?.computedNumber) {
      currentRun = null;
      continue;
    }

    if (meta.rawText && isAlreadyNumbered(meta.rawText)) {
      currentRun = null;
      continue;
    }

    prependComputedNumber(el, meta.computedNumber);
    stats.numbersApplied++;

    if (meta.ilvl != null && meta.ilvl > 0) {
      applyIlvlIndent(el, meta.ilvl);
      stats.multilevelApplied++;
    }

    const visibleCounter = parseLeadingCounter(meta.computedNumber);

    const sameRun =
      currentRun &&
      currentRun.numId === meta.numId &&
      currentRun.ilvl === (meta.ilvl ?? 0) &&
      !shouldStartNewDocxInstance(currentRun, meta, visibleCounter);

    if (sameRun) {
      currentRun!.elements.push(el);
      if (visibleCounter != null) {
        currentRun!.lastCounter = visibleCounter;
      }
    } else {
      const isNewInstance = shouldStartNewDocxInstance(currentRun, meta, visibleCounter);
      currentRun = {
        numId: meta.numId!,
        ilvl: meta.ilvl ?? 0,
        numFmt: meta.numFmt,
        elements: [el],
        startValue: visibleCounter,
        isNewInstance,
        lastCounter: visibleCounter,
      };
      listRuns.push(currentRun);
    }
  }

  stats.listsStructured = structureDecimalListRuns(doc, listRuns);

  return { html: root.innerHTML.trim(), stats };
}
